import type { ReactNode } from 'react';

export type BannerTone = 'info' | 'success' | 'warning' | 'error';

export interface Notice {
  tone: BannerTone;
  text: string;
}

interface BannerProps {
  tone: BannerTone;
  title?: string;
  /** False inside a live region the page keeps mounted, so regions don't nest. */
  live?: boolean;
  children: ReactNode;
}

// The inline banner pattern of the engagement Hub (no componentsv2 primitive),
// with the alert palette's light and dark tints.
const TONE_CLASSES: Record<BannerTone, string> = {
  info: 'border-brand-primary-faint bg-brand-light text-on-surface',
  success:
    'border-green-700/40 bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-200',
  warning:
    'border-yellow-700/40 bg-yellow-50 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200',
  error: 'border-red-700/40 bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-200',
};

export default function Banner({ tone, title, live = true, children }: BannerProps): JSX.Element {
  let role: 'alert' | 'status' | undefined;
  if (live) role = tone === 'error' ? 'alert' : 'status';
  return (
    <div
      role={role}
      className={`flex flex-col gap-4 rounded-md border px-16 py-12 ${TONE_CLASSES[tone]}`}
    >
      {title ? <span className="font-semibold subtitle-1">{title}</span> : null}
      <span className="body-1">{children}</span>
    </div>
  );
}
